/*
 * Copyright (C) 2025 Netgem SA
 *
 * This software is the copyrighted work of Netgem and constitutes
 * confidential information.
 *
 * By using this software you agree to hold the Confidential Information in
 * strict confidence and shall not disclose such information to any third party
 * without the written permission of Netgem SA. You also agree to employ all
 * steps necessary to protect the Confidential Information from unauthorized
 * disclosure or use, including without limitation, all steps you use to protect
 * information considered proprietary and a trade secret.
 */

import { logout } from '@app/actions';
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { ParsingError } from '@common/utils/errors';
import { apiPaths } from './apiPaths';

const arrayParamsSerializer = (params: object) => {
    const parts: string[] = [];

    Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((item) =>
                parts.push(`${key}=${encodeURIComponent(item)}`),
            );
        } else if (value) {
            parts.push(`${key}=${encodeURIComponent(value)}`);
        }
    });

    return parts.join('&');
};

const baseQuery = fetchBaseQuery({
    baseUrl: apiPaths.baseUrl(),
    credentials: 'include',
    paramsSerializer: arrayParamsSerializer,
    // prepareHeaders: (headers) => setHeaders(headers),
});

const baseQueryWithReauth: typeof baseQuery = async (
    args,
    api,
    extraOptions,
) => {
    const result = await baseQuery(args, api, extraOptions);

    if (result.error) {
        const status =
            typeof result.error.status === 'number'
                ? result.error.status
                : (result.error as ParsingError).originalStatus;
        if (status === 401) {
            console.warn(
                'Unauthorized (401) error detected. Clearing user state.',
            );

            api.dispatch(baseApi.util.resetApiState());
            api.dispatch(logout());
        }
    }

    return result;
};

const baseQueryWithRetry = retry(baseQueryWithReauth, { maxRetries: 0 });

export const baseApi = createApi({
    reducerPath: 'baseApi',
    baseQuery: baseQueryWithRetry,
    endpoints: () => ({}),
    tagTypes: ['Authentication', 'Collection', 'CollectionAssetIds', 'Clip'],
});
